import { Avatar } from 'antd'
import Skeleton from 'react-loading-skeleton'

import B2becTranslation from '@/components/B2becTranslation'
import CustomButton from '@/components/CustomButton'
import FormatPrice from '@/components/FormatPrice'
import { InfoIcon } from '@/components/Icons'
import { itemCategories } from '@/libs/constants/cart'
import AddProductImage from '@/assets/icons/addproduct.svg?react'

import CartItemName from '../CartItemName'
import RemoveCartItem from '../RemoveCartItem'
import CartItemQuantityWrapper from './CartItemQuantityWrapper'

const hasQuantityChanged = (record) => {
  if (record?.quantity === undefined || record?.reqQuantity === undefined) {
    return false
  }

  return record?.quantity !== record?.reqQuantity && !record.error
}

const renderAnnouncement = (record) => {
  const { materialNumber, replacedByMaterialNumber, hasMinQuantity } = record

  // OLD PRODUCT SECTION - record has `replacedByMaterialNumber`
  if (replacedByMaterialNumber) {
    return (
      <div className="replaced-annoucement">
        <B2becTranslation
          isComponent
          htmlTags={[<strong key="strong" />]}
          variables={{
            materialNumber,
            replacedByMaterialNumber,
          }}
          value="cart.replacedAnnoucement"
        />
      </div>
    )
  }

  if (hasMinQuantity) {
    return (
      <div className="moq-annoucement">
        <B2becTranslation
          value="cart.moqHintMessages"
          isComponent
          htmlTags={[<strong key="strong" />]}
        />
      </div>
    )
  }

  if (hasQuantityChanged(record)) {
    return (
      <div className="moq-annoucement">
        <B2becTranslation
          value="cart.quantityChanged"
          isComponent
          htmlTags={[<strong key="strong" />]}
        />
      </div>
    )
  }

  return null
}

const tableColumns = ({
  isLoading,
  handleCellClick,
  shouldShowRecyclingFee,
  t,
  confirmReplacementHandler,
  isInsideSupportArea,
  confirmMoqHandler,
  shouldDisplayVoucherLevel,
  currencyUnit,
  hasDeleteProductFromCartPermission,
  hasViewIndividualPricePermission,
}) => {
  const isDisabledOnClick = (materialNumber) => isLoading ? null : handleCellClick(materialNumber)

  const isOldProductSection = (record) => !!record?.replacedByMaterialNumber
  const isReplacedSection = (record) => !!record?.oldMaterialNumber
  const hasMOQ = (record) => !!record?.hasMinQuantity

  const isQuickEntryRow = (materialNumber) => typeof materialNumber !== 'string'

  return [
    {
      dataIndex: 'imgUrl',
      onCell: ({ materialNumber, disabled, ...record }) => ({
        onClick: () => {
          if (isReplacedSection(record)) {
            return null
          }
          return disabled ? null : isDisabledOnClick(materialNumber)
        },
      }),
      render: (text, record) => {
        const renderRemoveButton = () => {
          if (isReplacedSection(record)) {
            // NOTED: when removing item in replacement section
            // the old product will be removed, not the new one
            // so we pass the OLD MATERIAL NUMBER to the removal function
            return (
              <RemoveCartItem
                isOnFlexStart
                materialNumber={record?.oldMaterialNumber}
              />
            )
          }
          if (hasMOQ(record)) {
            return (
              <RemoveCartItem
                isOnFlexStart
                materialNumber={record?.materialNumber}
              />
            )
          }
          return null
        }

        return isLoading ? <Skeleton /> : (
          <>
            {renderAnnouncement(record)}
            <Avatar shape="square" size={80} src={text || AddProductImage} />
            {!isInsideSupportArea && renderRemoveButton()}
          </>
        )
      },
    },
    {
      title: t('cart.table.itemNo'),
      dataIndex: 'materialNumber',
      onCell: ({ materialNumber, disabled }) => ({
        onClick: () => (disabled ? null : isDisabledOnClick(materialNumber)),
      }),
      render: (text, { error }) => {
        if (isLoading) {
          return <Skeleton />
        }

        return error
          ? (
            <>
              <InfoIcon style={{ verticalAlign: 'middle' }} /> {text}
            </>
          )
          : <span>{text}</span>
      },
    },
    {
      title: t('cart.table.itemName'),
      dataIndex: 'name',
      onCell: ({ materialNumber, disabled }) => ({
        onClick: () => (disabled ? null : isDisabledOnClick(materialNumber)),
      }),
      render: (_, record) =>
        isLoading ? <Skeleton /> : (
          <CartItemName
            data={record}
            shouldShowRecyclingFee={shouldShowRecyclingFee}
          />
        ),
    },
    {
      title: t('cart.table.quantity'),
      dataIndex: 'quantity',
      align: 'center',
      render: (
        _,
        {
          materialNumber,
          quantity,
          error,
          hasMinQuantity,
          originalMaterial,
          itemCateg,
          ...record
        },
      ) => {
        const isBonusItem = !!originalMaterial && itemCateg === itemCategories.bonusMaterial

        const quantityPart = (productId, isShowRemovePart = true) => {
          const shouldShowRemovePart = hasDeleteProductFromCartPermission
            && isShowRemovePart
            && !isInsideSupportArea
            && !isBonusItem
          const shouldDisableQuantityPart = !!error || isInsideSupportArea || isBonusItem

          return (
            <>
              <CartItemQuantityWrapper
                hasMinQuantity={hasMinQuantity}
                isParentDisabled={shouldDisableQuantityPart}
                materialNumber={productId}
                quantity={quantity}
              />
              {shouldShowRemovePart && <RemoveCartItem materialNumber={productId} />}
            </>
          )
        }

        const renderQuantityPart = () => {
          if (isOldProductSection(record) || isQuickEntryRow(materialNumber)) {
            return null
          }
          if (isReplacedSection(record)) {
            return quantityPart(record?.oldMaterialNumber, false)
          }
          if (hasMinQuantity) {
            return quantityPart(materialNumber, false)
          }

          return quantityPart(materialNumber)
        }

        return isLoading ? <Skeleton /> : renderQuantityPart()
      },
    },
    {
      title: hasViewIndividualPricePermission
        ? t('cart.table.individualPrice')
        : '',
      dataIndex: 'price',
      align: 'right',
      onCell: ({ materialNumber, disabled }) => ({
        onClick: () => (disabled ? null : isDisabledOnClick(materialNumber)),
      }),
      render: (
        text,
        { quantity, reqQuantity, materialNumber, discount, ...record },
      ) => {
        if (!hasViewIndividualPricePermission) {
          return null
        }
        const renderInvidualPrice = () => {
          if (isOldProductSection(record) || hasQuantityChanged(record)) {
            return null
          }
          return (
            <>
              <FormatPrice currencyUnit={currencyUnit} price={text} />

              {shouldDisplayVoucherLevel({ discount })
                ? (
                  <div className="voucher-text">
                    {t('cart.table.includedVoucher')}
                  </div>
                )
                : null}
            </>
          )
        }

        return isLoading ? <Skeleton /> : renderInvidualPrice()
      },
    },
    {
      title: t('total'),
      dataIndex: 'total',
      align: 'right',
      hidden: !hasViewIndividualPricePermission,
      onCell: ({ materialNumber, disabled, ...record }) => ({
        onClick: () => {
          if (isReplacedSection(record) || disabled) {
            return null
          }
          return isDisabledOnClick(materialNumber)
        },
      }),
      render: (
        text,
        { quantity, reqQuantity, materialNumber, discount, ...record },
      ) => {
        const renderConfirmButton = () => {
          if (isReplacedSection(record)) {
            return (
              <CustomButton
                className="border-section--confirm-btn"
                onClick={() =>
                  confirmReplacementHandler(
                    record?.oldMaterialNumber,
                    materialNumber,
                    quantity,
                  )}
              >
                {t('buttonTexts.confirm')}
              </CustomButton>
            )
          }

          if (hasQuantityChanged(record)) {
            return (
              <CustomButton
                className="border-section--confirm-btn"
                onClick={(e) =>
                  confirmMoqHandler(e, {
                    materialNumber,
                    quantity,
                  })}
              >
                {t('buttonTexts.confirm')}
              </CustomButton>
            )
          }

          return null
        }

        const renderTotalPrice = () => {
          if (isOldProductSection(record) || hasQuantityChanged(record)) {
            return null
          }

          return (
            <div className="price-column">
              <FormatPrice
                className="price-column__price boldText"
                currencyUnit={currencyUnit}
                price={text}
              />

              {shouldDisplayVoucherLevel({ discount })
                ? (
                  <FormatPrice
                    className="price-column__price--voucher"
                    currencyUnit={currencyUnit}
                    price={discount}
                  />
                )
                : null}
            </div>
          )
        }

        return isLoading ? <Skeleton /> : (
          <>
            {renderTotalPrice()}
            {!isInsideSupportArea && renderConfirmButton()}
          </>
        )
      },
    },
  ].filter((col) => !col.hidden)
}
export default tableColumns
